<template>
  <div class="checkout-container">
    <stepper-progress :current-step="3"></stepper-progress>

    <div class="payment-wrapper">
      <v-layout row wrap>
        <v-flex md3 class="sidebar-col">
          <side-bar></side-bar>
        </v-flex>

        <v-flex md8 ml-5 class="main-content">
          <!-- Donations Display -->
          <div class="donation-card" v-if="loadedDonationsList.length > 0">
            <h4 class="earth-color donation-title">
              <v-icon color="earth">mdi-heart</v-icon>
              YOUR DONATIONS
            </h4>
            <v-list class="donations-list" three-line>
              <v-list-item v-for="(item, index) in loadedDonationsList" :key="index" class="donation-list-item">
                <v-list-item-content>
                  <v-list-item-title class="donation-item-title">
                    <strong>{{ item.currency_name }} {{ formatNumber(item.amount) }}</strong>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ item.cause_name || item.cause || 'Conservation Donation' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon color="error" @click="removeDonation(index)" small>
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div class="donations-total" v-if="loadedDonationsTotal > 0">
              Total Donations: <strong>{{ browserCurrency }} {{ formatNumber(loadedDonationsTotal) }}</strong>
            </div>
          </div>

          <!-- Add Donation -->
          <div class="donation-card">
            <h4 class="earth-color donation-title">
              <v-icon color="earth">mdi-heart</v-icon>
              ADD ADDITIONAL DONATION TO CONSERVATION EFFORTS
            </h4>
            <v-layout row wrap class="donation-form">
              <v-flex lg6 md6 sm12 xs12>
                <v-container>
                  <v-autocomplete label="Currency"
                                  v-model="on_payment_donation.donation_currency_obj"
                                  :items="donationCurrencies"
                                  @change="changeCurrencyDonation"
                                  :return-object="true"
                                  item-text="currency_name"
                                  item-value="currency_public_id"
                                  prepend-icon="mdi-currency-sign"
                                  outlined
                                  dense
                                  :disabled="true"></v-autocomplete>
                  <div class="caption text-muted mt-1" style="font-size: 0.75rem; color: #666;">
                    Donations are in {{ bookingCurrency }} (booking currency)
                  </div>
                </v-container>
              </v-flex>
              <v-flex lg6 md6 sm12 xs12>
                <v-container>
                  <v-text-field label="Amount"
                                v-model="on_payment_donation.donation_amount"
                                type="number"
                                prepend-icon="mdi-cash"
                                outlined
                                dense
                                :placeholder="'Enter amount in ' + bookingCurrency"></v-text-field>
                </v-container>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex lg12 md12 sm12 xs12>
                <v-container class="donation-button-container">
                  <v-btn :depressed="true"
                         color="primary"
                         @click="addOnlinePaymentDonation"
                         class="donate-btn full-width-btn"
                         :disabled="!on_payment_donation.donation_amount || on_payment_donation.donation_amount <= 0 || !on_payment_donation.donation_currency_obj">
                    <v-icon left>mdi-plus</v-icon>
                    Add Donation in {{ bookingCurrency }}
                  </v-btn>
                </v-container>
              </v-flex>
            </v-layout>
          </div>

          <!-- Wallet Balance Info -->
          <div class="wallet-info-card" v-if="isUserLoggedIn">
            <h4 class="earth-color wallet-title">
              <v-icon color="earth">mdi-wallet</v-icon>
              YOUR WALLET BALANCE
              <v-btn small text color="primary" @click="refreshWalletBalance" :loading="walletLoading" class="ml-2">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </h4>
            <div class="wallet-balances">
              <div class="wallet-balance-item">
                <span class="balance-label">KES Balance:</span>
                <span class="balance-amount" :class="{ 'text-success': walletBalanceKes >= totalPaymentAmount, 'text-danger': walletBalanceKes < totalPaymentAmount }">
                  {{ formatNumber(walletBalanceKes) }} KES
                </span>
              </div>
              <div class="wallet-balance-item">
                <span class="balance-label">USD Balance:</span>
                <span class="balance-amount" :class="{ 'text-success': walletBalanceUsd >= totalPaymentAmountUSD, 'text-danger': walletBalanceUsd < totalPaymentAmountUSD }">
                  {{ formatNumber(walletBalanceUsd) }} USD
                </span>
              </div>
            </div>
          </div>

          <!-- Booking Summary -->
          <div class="booking-summary-card" v-if="singleBooking">
            <h4 class="earth-color summary-title">
              <v-icon color="earth">mdi-receipt</v-icon>
              BOOKING SUMMARY
            </h4>
            <div class="summary-details">
              <div class="summary-row">
                <span>Booking REF:</span>
                <strong>{{ singleBooking.booking_ref_code || singleBooking.booking_public_id }}</strong>
              </div>
              <div class="summary-row">
                <span>Check-in:</span>
                <strong>{{ formatDate(singleBooking.check_in_date) }}</strong>
              </div>
              <div class="summary-row">
                <span>Check-out:</span>
                <strong>{{ formatDate(singleBooking.check_out_date) }}</strong>
              </div>
              <div class="summary-row total-row">
                <span>Total Amount:</span>
                <strong class="total-amount">{{ browserCurrency }} {{ formatNumber(bookingTotal) }}</strong>
              </div>
              <div class="summary-row" v-if="loadedDonationsTotal > 0">
                <span>Donations Added:</span>
                <strong class="donation-amount">+ {{ browserCurrency }} {{ formatNumber(loadedDonationsTotal) }}</strong>
              </div>
              <div class="summary-row grand-total-row" v-if="loadedDonationsTotal > 0">
                <span>Grand Total:</span>
                <strong class="grand-total-amount">{{ browserCurrency }} {{ formatNumber(totalPaymentAmount) }}</strong>
              </div>
            </div>
          </div>

          <!-- Payment Methods -->
          <div class="payment-methods">
            <h3 class="earth-color methods-title">SELECT PAYMENT METHOD</h3>
            <v-layout row wrap class="method-cards">

              <!-- Wallet Payment Option -->
              <v-flex lg4 md4 sm12 xs12 class="mb-4" v-if="isUserLoggedIn">
                <v-card class="method-card"
                        :class="{
                          'active-method': payment_mode === 'Wallet',
                          'selected-method': payment_mode === 'Wallet',
                          'disabled-method': !hasSufficientWalletBalance
                        }"
                        @click="selectWalletPayment">
                  <div class="method-icon">
                    <v-icon size="70" color="#74a533">mdi-wallet</v-icon>
                  </div>
                  <v-card-title primary-title>
                    <div>
                      <h3 class="earth-color">Pay via Wallet</h3>
                      <div class="method-description">
                        Use your wallet balance
                      </div>
                      <div class="wallet-balance-small">
                        <span>KES: {{ formatNumber(walletBalanceKes) }}</span>
                        <span v-if="walletBalanceUsd > 0" class="ml-2">USD: {{ formatNumber(walletBalanceUsd) }}</span>
                      </div>
                      <div v-if="hasSufficientWalletBalance" class="sufficient-text">
                        <v-icon small color="success">mdi-check-circle</v-icon>
                        Sufficient {{ bookingCurrency }} balance
                      </div>
                      <div v-else class="insufficient-text">
                        <v-icon small color="error">mdi-alert</v-icon>
                        Need: {{ formatNumber(bookingCurrency === 'USD' ? totalPaymentAmountUSD : totalPaymentAmount) }} {{ bookingCurrency }}
                      </div>
                    </div>
                  </v-card-title>
                  <v-card-actions class="method-selection">
                    <v-radio v-model="payment_mode"
                             value="Wallet"
                             color="primary"
                             :disabled="!hasSufficientWalletBalance"></v-radio>
                    <div class="selected-indicator" v-if="payment_mode === 'Wallet'">
                      <v-icon color="primary">mdi-check-circle</v-icon>
                      <span>Selected</span>
                    </div>
                  </v-card-actions>
                </v-card>
              </v-flex>

              <!-- Mpesa Payment Option -->
              <v-flex lg4 md4 sm12 xs12 class="mb-4" v-if="paymentKESPrice < 250000 && paymentKESPrice > 0">
                <v-card class="method-card"
                        :class="{ 'active-method': payment_mode === 'Mpesa', 'selected-method': payment_mode === 'Mpesa' }"
                        @click="payment_mode = 'Mpesa'">
                  <div class="method-icon">
                    <img src="https://marketplace.datingpro.com/image/data/payment%20systems/m-pesa-integration.png"
                         alt="m" />
                  </div>
                  <v-card-title primary-title>
                    <div>
                      <h3 class="earth-color">Pay via Mpesa</h3>
                      <div class="method-description">
                        Instant mobile payments for Kenya
                      </div>
                      <div class="method-limit">Under KES 250,000</div>
                    </div>
                  </v-card-title>
                  <v-card-actions class="method-selection">
                    <v-radio v-model="payment_mode"
                             value="Mpesa"
                             color="primary"
                             :input-value="payment_mode === 'Mpesa'"></v-radio>
                    <div class="selected-indicator" v-if="payment_mode === 'Mpesa'">
                      <v-icon color="primary">mdi-check-circle</v-icon>
                      <span>Selected</span>
                    </div>
                  </v-card-actions>
                </v-card>
              </v-flex>

              <!-- Card Payment Option -->
              <v-flex lg4 md4 sm12 xs12 class="mb-4">
                <v-card class="method-card"
                        :class="{ 'active-method': payment_mode === 'Card', 'selected-method': payment_mode === 'Card' }"
                        @click="payment_mode = 'Card'">
                  <div class="method-icon">
                    <img src="https://www.freeiconspng.com/uploads/credit-cards-icon-25.png"
                         alt="Credit Card" />
                  </div>
                  <v-card-title primary-title>
                    <div>
                      <h3 class="earth-color">Pay via Card</h3>
                      <div class="method-description">
                        Visa, Mastercard & other cards
                      </div>
                    </div>
                  </v-card-title>
                  <v-card-actions class="method-selection">
                    <v-radio v-model="payment_mode"
                             value="Card"
                             color="primary"
                             :input-value="payment_mode === 'Card'"></v-radio>
                    <div class="selected-indicator" v-if="payment_mode === 'Card'">
                      <v-icon color="primary">mdi-check-circle</v-icon>
                      <span>Selected</span>
                    </div>
                  </v-card-actions>
                </v-card>
              </v-flex>
            </v-layout>
          </div>

          <!-- Wallet Payment Form -->
          <transition name="slide-fade">
            <div v-if="payment_mode === 'Wallet'" class="payment-form">
              <v-card class="form-card">
                <v-card-title class="form-title earth-color">
                  <v-icon left color="earth">mdi-wallet</v-icon>
                  WALLET PAYMENT
                </v-card-title>
                <v-container>
                  <div class="wallet-payment-info">
                    <v-alert :type="hasSufficientWalletBalance ? 'success' : 'error'" :value="true" class="mb-4">
                      <div class="payment-details">
                        <div>
                          <strong>Total to Pay:</strong>
                          {{ bookingCurrency }} {{ formatNumber(bookingCurrency === 'USD' ? totalPaymentAmountUSD : totalPaymentAmount) }}
                          <span class="text-muted ml-2" v-if="bookingCurrency === 'USD' && browserCurrency === 'KES'">
                            (≈ {{ browserCurrency }} {{ formatNumber(totalPaymentAmount) }})
                          </span>
                        </div>
                        <div><strong>Your KES Balance:</strong> {{ formatNumber(walletBalanceKes) }} KES</div>
                        <div><strong>Your USD Balance:</strong> {{ formatNumber(walletBalanceUsd) }} USD</div>
                        <div v-if="hasSufficientWalletBalance" class="mt-2 text-success">
                          <v-icon small>mdi-check-circle</v-icon> You have sufficient {{ bookingCurrency }} balance
                        </div>
                        <div v-else class="mt-2 text-error">
                          <v-icon small>mdi-alert-circle</v-icon> Insufficient {{ bookingCurrency }} balance.
                          Need: {{ formatNumber(bookingCurrency === 'USD' ? totalPaymentAmountUSD : totalPaymentAmount) }} {{ bookingCurrency }}
                        </div>
                      </div>
                    </v-alert>

                    <div class="payment-breakdown mt-4">
                      <div class="breakdown-item">
                        <span>Booking Amount:</span>
                        <span>{{ bookingCurrency }} {{ formatNumber(bookingAmountOriginal) }}</span>
                        <span class="text-muted ml-2" v-if="bookingCurrency === 'USD' && browserCurrency === 'KES'">
                          (≈ {{ browserCurrency }} {{ formatNumber(convertCurrency('USD', 'KES', bookingAmountOriginal)) }})
                        </span>
                      </div>
                      <div v-if="loadedDonationsList.length > 0" class="breakdown-item">
                        <span>Donations:</span>
                        <span>
                          <span v-for="(item, idx) in loadedDonationsList" :key="idx">
                            {{ item.currency_name }} {{ formatNumber(item.amount) }}
                            <span v-if="idx < loadedDonationsList.length - 1"> + </span>
                          </span>
                        </span>
                      </div>
                      <div class="breakdown-item total">
                        <span>Total:</span>
                        <span>
                          <strong>
                            {{ bookingCurrency }} {{ formatNumber(bookingCurrency === 'USD' ? totalPaymentAmountUSD : totalPaymentAmount) }}
                          </strong>
                          <span class="text-muted ml-2" v-if="bookingCurrency === 'USD' && browserCurrency === 'KES'">
                            (≈ {{ browserCurrency }} {{ formatNumber(totalPaymentAmount) }})
                          </span>
                        </span>
                      </div>
                    </div>

                    <div class="payment-actions mt-4">
                      <v-btn :depressed="true"
                             type="submit"
                             color="primary"
                             block
                             large
                             @click="processWalletPayment"
                             :loading="processingWalletPayment"
                             :disabled="!hasSufficientWalletBalance || processingWalletPayment">
                        <v-icon left>mdi-wallet</v-icon>
                        PAY {{ formatNumber(bookingCurrency === 'USD' ? totalPaymentAmountUSD : totalPaymentAmount) }} {{ bookingCurrency }} FROM WALLET
                      </v-btn>
                    </div>

                    <div class="payment-security mt-3">
                      <v-icon color="green">mdi-shield-check</v-icon>
                      <span>Instant payment confirmation - No additional fees</span>
                    </div>
                  </div>
                </v-container>
              </v-card>
            </div>
          </transition>

          <!-- Card Payment Form -->
          <transition name="slide-fade">
            <div v-if="payment_mode === 'Card'" class="payment-form">
              <v-card class="form-card">
                <v-card-title class="form-title earth-color">
                  <v-icon left color="earth">mdi-credit-card</v-icon>
                  CARD PAYMENT DETAILS
                </v-card-title>
                <v-container>
                  <form id="myform"
                        name="myform"
                        method="POST"
                        action="https://portal.host.iveri.com/Lite/Authorise.aspx" target="_blank">
                    <input v-if="singleBooking && singleBooking.booking_currency_name === 'USD'"
                           id="Lite_Merchant_Applicationid"
                           name="Lite_Merchant_Applicationid"
                           type="hidden"
                           :value="live_id"
                           readonly="true" />
                    <input v-if="singleBooking && singleBooking.booking_currency_name === 'KES'"
                           id="Lite_Merchant_Applicationid"
                           name="Lite_Merchant_Applicationid"
                           type="hidden"
                           :value="kes_live_id"
                           readonly="true" />
                    <input id="Lite_Order_Amount"
                           name="Lite_Order_Amount"
                           type="hidden"
                           :value="bookingPaymentUSDPrice"
                           readonly="true" />
                    <input type="hidden"
                           name="Lite_Website_Successful_url"
                           :value="iveri_catcher_url + 'booking-payment'" />
                    <input type="hidden"
                           name="Lite_Website_Fail_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input type="hidden"
                           name="Lite_Website_TryLater_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input type="hidden"
                           name="Lite_Website_Error_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input id="Lite_Currency_AlphaCode"
                           name="Lite_Currency_AlphaCode"
                           type="hidden"
                           :value="singleBooking ? singleBooking.booking_currency_name : 'KES'"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Product_1"
                           name="Lite_Order_LineItems_Product_1"
                           type="hidden"
                           value="Booking Total"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Quantity_1"
                           name="Lite_Order_LineItems_Quantity_1"
                           type="hidden"
                           value="1"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Amount_1"
                           name="Lite_Order_LineItems_Amount_1"
                           type="hidden"
                           :value="bookingPaymentUSDPrice"
                           readonly="true" />
                    <input id="Lite_ConsumerOrderID_PreFix"
                           name="Lite_ConsumerOrderID_PreFix"
                           type="hidden"
                           value="DML"
                           readonly="true" />
                    <input id="Ecom_BillTo_Online_Email"
                           name="Ecom_BillTo_Online_Email"
                           type="hidden"
                           :value="paymentEmail" />
                    <input id="Ecom_Payment_Card_Protocols"
                           name="Ecom_Payment_Card_Protocols"
                           type="hidden"
                           value="iVeri"
                           readonly="true" />
                    <input id="BOOKINGID"
                           name="BOOKINGID"
                           type="hidden"
                           :value="singleBooking ? singleBooking.booking_public_id : $route.params.booking_id" />
                    <input id="PAYMENTREF" name="PAYMENTREF" type="hidden" :value="cardPaymentReference" />
                    <input id="Ecom_TransactionComplete"
                           name="Ecom_TransactionComplete"
                           type="hidden"
                           value
                           readonly="true" />

                    <v-text-field id="txtEmail"
                                  name="txtEmail"
                                  v-model="paymentEmail"
                                  :rules="requiredRules"
                                  label="Email"
                                  required
                                  prepend-icon="mdi-email"
                                  outlined
                                  dense></v-text-field>

                    <div class="payment-actions">
                      <v-btn :depressed="true"
                             type="submit"
                             id="btnsubmit"
                             class="btn primary"
                             name="btnsubmit"
                             @click="checkForPaymentDonation()"
                             color="primary"
                             large
                             block>
                        <v-icon left>mdi-lock</v-icon>
                        PROCEED TO SECURE PAYMENT
                      </v-btn>
                    </div>

                    <div class="payment-security">
                      <v-icon color="green">mdi-shield-check</v-icon>
                      <span>Your payment is secured with 256-bit encryption</span>
                    </div>

                    <div class="email-confirmation earth-color">
                      <v-icon color="earth">mdi-alert-circle</v-icon>
                      PLEASE RE-CONFIRM YOUR EMAIL ADDRESS TO PROCEED WITH PAYMENT
                    </div>
                  </form>
                </v-container>
              </v-card>
            </div>
          </transition>

          <!-- Mpesa Payment Form -->
          <transition name="slide-fade">
            <div v-if="payment_mode === 'Mpesa'" class="payment-form">
              <v-card class="form-card">
                <v-card-title class="form-title earth-color">
                  <v-icon left color="earth">mdi-cellphone</v-icon>
                  MPESA PAYMENT
                </v-card-title>
                <v-container>
                  <div class="mpesa-notice earth-color">
                    <v-icon color="earth">mdi-information</v-icon>
                    KINDLY NOTE THAT PAYMENTS ABOVE KES 250,000 CANNOT BE PROCESSED
                  </div>

                  <v-text-field v-model="mpesa.phone_number"
                                :rules="requiredRules"
                                label="Payer's Safaricom number"
                                placeholder="e.g., 254712345678"
                                required
                                prepend-icon="mdi-phone"
                                outlined
                                dense></v-text-field>

                  <div class="payment-actions">
                    <v-btn :depressed="true"
                           type="submit"
                           id="btnsubmit"
                           class="btn primary"
                           name="btnsubmit"
                           color="primary"
                           block
                           large
                           @click="requestPayment()"
                           :loading="payment_requested">
                      <v-icon left>mdi-send</v-icon>
                      REQUEST PAYMENT
                    </v-btn>
                  </div>

                  <transition name="fade">
                    <div v-if="payment_requested === true" class="confirm-payment">
                      <v-btn :depressed="true"
                             color="warning"
                             block
                             large
                             @click="checkPayment()"
                             :loading="$loadingTrue">
                        <v-icon left>mdi-check-circle</v-icon>
                        CONFIRM PAYMENT
                      </v-btn>
                      <div class="payment-instructions">
                        Check your phone for M-Pesa prompt and enter your PIN to complete payment
                      </div>
                    </div>
                  </transition>
                </v-container>
              </v-card>
            </div>
          </transition>
        </v-flex>
      </v-layout>
    </div>
    {{ redirectIfPaid }}
    {{ currencyChangeBasedOnPaymentType }}
  </div>
</template>

<script>
  import SideBar from '../../Public/Repeated/SideBar.vue'
  import StepperProgress from './StepperProgress.vue';

  export default {
    components: {
      SideBar,
      StepperProgress
    },
    name: "GuestPaymentInformation",
    data: function () {
      return {
        on_payment_donation: {
          donation_currency_obj: null,
          donation_currency_id: "",
          donation_currency_name: "", // Empty - will be set dynamically
          donation_amount: "",
          donation_currency_buy_rate: "",
          donation_currency_sell_rate: "",
        },
        requiredRules: [(v) => !!v || "This field is required"],
        payment_requested: false,
        card_button: false,
        items: "",
        mpesa: {
          phone_number: "",
        },
        payment_mode: "",
        iveri_url: "https://bookings.olpejetaconservancy.org/",
        iveri_catcher_url: "https://bookings.olpejetaconservancy.org:5019/",
        loadedDonationsList: [],
        live_id: "c3d68cda-85f3-44c1-b578-6609e86e305e",
        kes_live_id: "a98a2520-77f0-4ff9-a0a5-29ee81852fef",
        paymentEmail: "",
        cardPaymentReference: '',
        selectedPaymentCurrency: 'KES',
        processingWalletPayment: false,
        walletBalanceUsd: 0,
        walletBalanceKes: 0,
        walletLoading: false,
        currencySet: false,
      };
    },
    computed: {
      userFullName: function () {
        var firstName = localStorage.getItem('user_first_name') || localStorage.getItem('first_name') || '';
        var lastName = localStorage.getItem('user_last_name') || localStorage.getItem('last_name') || '';
        return (firstName + ' ' + lastName).trim() || 'Guest User';
      },

      userFirstName: function () {
        return localStorage.getItem('user_first_name') || localStorage.getItem('first_name') || '';
      },

      userLastName: function () {
        return localStorage.getItem('user_last_name') || localStorage.getItem('last_name') || '';
      },

      userEmail: function () {
        return localStorage.getItem('user_email') || localStorage.getItem('email') || '';
      },

      userPhone: function () {
        var phone = localStorage.getItem('user_phone') || localStorage.getItem('user_phone_number') || localStorage.getItem('phone') || '';
        if (phone && !this.mpesa.phone_number) {
          this.mpesa.phone_number = phone;
        }
        return phone;
      },

      isUserLoggedIn: function () {
        var userPublicId = localStorage.getItem('user_public_id');
        var userEmail = localStorage.getItem('user_email');
        return !!(userPublicId && userPublicId !== 'null' && userPublicId !== 'undefined' && userEmail);
      },

      bookingCurrency: function () {
        if (this.singleBooking) {
          return this.singleBooking.currency ||
            this.singleBooking.booking_currency_name ||
            'USD';
        }
        return 'USD';
      },

      bookingAmountOriginal: function () {
        if (this.singleBooking) {
          var amount = this.singleBooking.booking_payment_balance ||
            this.singleBooking.total_amount ||
            this.singleBooking.grand_total ||
            this.singleBooking.total ||
            this.singleBooking.amount ||
            0;
          return parseFloat(amount);
        }
        return 0;
      },

      loadedDonationsTotal: function () {
        var total = 0;
        var self = this;
        if (self.loadedDonationsList && self.loadedDonationsList.length > 0) {
          self.loadedDonationsList.forEach(function (donation) {
            total = total + (parseFloat(donation.amount) || 0);
          });
        }
        return total;
      },

      usdDonationsTotal: function () {
        var total = 0;
        if (this.loadedDonationsList.length > 0) {
          this.loadedDonationsList.forEach(function (donation) {
            if (donation.currency_name === 'USD') {
              total += parseFloat(donation.amount) || 0;
            }
          });
        }
        return total;
      },

      kesDonationsTotal: function () {
        var total = 0;
        if (this.loadedDonationsList.length > 0) {
          this.loadedDonationsList.forEach(function (donation) {
            if (donation.currency_name === 'KES') {
              total += parseFloat(donation.amount) || 0;
            }
          });
        }
        return total;
      },

      usdToKesRate: function () {
        var exchanges = this.$store.state.CurrencyStore.exchanges || [];
        var rate = 130;
        exchanges.forEach(function (exchange) {
          if (exchange.currency && exchange.currency.toLowerCase() === 'usd') {
            rate = parseFloat(exchange.sell_amount) || 130;
          }
        });
        return rate;
      },

      kesToUsdRate: function () {
        var exchanges = this.$store.state.CurrencyStore.exchanges || [];
        var rate = 0.0077;
        exchanges.forEach(function (exchange) {
          if (exchange.currency && exchange.currency.toLowerCase() === 'usd') {
            rate = parseFloat(exchange.buy_amount) || 0.0077;
          }
        });
        return rate;
      },

      totalPaymentAmountUSD: function () {
        var totalUSD = this.bookingAmountOriginal;
        totalUSD += this.usdDonationsTotal;
        if (this.kesDonationsTotal > 0) {
          totalUSD += (this.kesDonationsTotal / this.kesToUsdRate);
        }
        return totalUSD;
      },

      totalPaymentAmount: function () {
        if (this.bookingCurrency === 'USD') {
          return this.totalPaymentAmountUSD * this.usdToKesRate;
        } else {
          var totalKES = this.bookingAmountOriginal;
          if (this.usdDonationsTotal > 0) {
            totalKES += this.usdDonationsTotal * this.usdToKesRate;
          }
          totalKES += this.kesDonationsTotal;
          return totalKES;
        }
      },

      singleBooking: function () {
        let booking = null;

        if (this.$store.state.BookingStore) {
          booking = this.$store.state.BookingStore.single_booking ||
            this.$store.state.BookingStore.booking ||
            this.$store.state.BookingStore.current_booking;
        }

        if (!booking && this.$store.state.booking) {
          booking = this.$store.state.booking.single_booking ||
            this.$store.state.booking.booking ||
            this.$store.state.booking.current_booking;
        }

        if (!booking && this.$store.state.BookingStore && this.$store.state.BookingStore.bookings) {
          let bookings = this.$store.state.BookingStore.bookings;
          if (bookings && bookings.length > 0) {
            booking = bookings.find(b => b.booking_public_id === this.$route.params.booking_id);
          }
        }

        return booking;
      },

      bookingTotal: function () {
        if (this.singleBooking) {
          var total = this.singleBooking.booking_payment_balance ||
            this.singleBooking.total_amount ||
            this.singleBooking.grand_total ||
            this.singleBooking.total ||
            this.singleBooking.amount ||
            0;
          return total;
        }
        return 0;
      },

      paymentKESPrice: function () {
        return this.bookingTotal;
      },

      hasSufficientWalletBalance: function () {
        if (this.bookingCurrency === 'USD') {
          return this.walletBalanceUsd >= this.totalPaymentAmountUSD;
        }
        return this.walletBalanceKes >= this.totalPaymentAmount;
      },

      bookingPaymentUSDPrice: function () {
        if (this.singleBooking && this.singleBooking.currency !== undefined) {
          var donationAmount = this.loadedDonationsTotal;
          if (this.singleBooking.currency === "KES") {
            var paying_amount = this.paymentKESPrice;
            var total_payment_kes = paying_amount + donationAmount;
            return total_payment_kes * 100;
          } else {
            return (this.singleBooking.booking_payment_balance + Math.ceil(donationAmount)) * 100;
          }
        }
        return 0;
      },

      browserCurrency: function () {
        if (this.$store.state.CurrencyStore && this.$store.state.CurrencyStore.browser_currency) {
          return this.$store.state.CurrencyStore.browser_currency;
        }
        return 'KES';
      },

      browserCurrencyID: function () {
        if (this.$store.state.CurrencyStore && this.$store.state.CurrencyStore.browser_currency_id) {
          return this.$store.state.CurrencyStore.browser_currency_id;
        }
        return '162fface-f5f1-41de-913b-d2bb784dda3a';
      },

      allCurrencies: function () {
        if (this.$store.state.CurrencyStore && this.$store.state.CurrencyStore.all_currencies) {
          return this.$store.state.CurrencyStore.all_currencies;
        }
        return [];
      },

      allExchanges: function () {
        return this.$store.state.CurrencyStore.exchanges || [];
      },

      donationCurrencies: function () {
        var bookingCurr = this.bookingCurrency;
        var all = this.allCurrencies || [];
        var filtered = all.filter(function (currency) {
          return currency.currency_name === bookingCurr;
        });
        return filtered.length > 0 ? filtered : all;
      },

      currencyChangeBasedOnPaymentType: function () {
        if (this.payment_mode === "Mpesa") {
          var kes = {
            created_at: null,
            currency_description: "The Kenyan Shilling.",
            currency_name: "KES",
            currency_public_id: "162fface-f5f1-41de-913b-d2bb784dda3a",
            currency_sign: "KES",
            session_id: "Admin",
            updated_at: null,
          };
          this.currencyChange(kes);
        } else if (this.payment_mode === "Card" && this.singleBooking) {
          var singleBooking = this.singleBooking;
          var currencyChange = this.currencyChange;
          if (this.allCurrencies && singleBooking && singleBooking.currency) {
            this.allCurrencies.forEach(function (currency) {
              if (currency.currency_name === singleBooking.currency) {
                currencyChange(currency);
              }
            });
          }
        }
        return null;
      },

      redirectIfPaid: function () {
        if (this.$route.name === "GuestSuccess") {
          return null;
        }

        if (this.singleBooking && this.singleBooking.booking_payment_status === "Paid") {
          this.$router.push({
            name: "GuestSuccess",
            params: { booking_id: this.$route.params.booking_id }
          });
        }
        return null;
      }
    },

    watch: {
      allCurrencies: function (newVal) {
        if (newVal && newVal.length > 0 && !this.currencySet) {
          this.setDefaultDonationCurrency();
        }
      },
      singleBooking: function (newVal) {
        if (newVal && this.allCurrencies.length > 0 && !this.currencySet) {
          this.setDefaultDonationCurrency();
        }
      },
      bookingCurrency: function (newVal, oldVal) {
        if (newVal && this.allCurrencies.length > 0) {
          this.setDefaultDonationCurrency();
        }
      }
    },

    methods: {
      formatNumber: function (value) {
        if (!value && value !== 0) return '0';
        return parseFloat(value).toLocaleString("en", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      },

      formatDate: function (date) {
        if (!date) return '';
        if (this.$moment) {
          return this.$moment(date).format('MMM DD, YYYY');
        }
        return date;
      },

      convertCurrency: function (fromCurrency, toCurrency, amount) {
        if (!amount || amount === 0) return 0;

        fromCurrency = fromCurrency.toUpperCase();
        toCurrency = toCurrency.toUpperCase();

        if (fromCurrency === toCurrency) return amount;

        var exchanges = this.allExchanges;
        var result = amount;

        if (fromCurrency !== 'KES') {
          var fromRate = 1;
          exchanges.forEach(function (exchange) {
            if (exchange.currency && exchange.currency.toUpperCase() === fromCurrency) {
              fromRate = parseFloat(exchange.sell_amount) || 1;
            }
          });
          result = result * fromRate;
        }

        if (toCurrency !== 'KES') {
          var toRate = 1;
          exchanges.forEach(function (exchange) {
            if (exchange.currency && exchange.currency.toUpperCase() === toCurrency) {
              toRate = parseFloat(exchange.buy_amount) || 1;
            }
          });
          result = result / toRate;
        }

        return result;
      },

      selectWalletPayment: function () {
        if (this.hasSufficientWalletBalance) {
          this.payment_mode = 'Wallet';
          this.$forceUpdate();
        } else {
          if (this.$toast) {
            if (this.bookingCurrency === 'USD') {
              this.$toast.error(`Insufficient USD wallet balance. Need $${this.totalPaymentAmountUSD.toFixed(2)} USD`);
            } else {
              this.$toast.error(`Insufficient wallet balance. Need ${this.formatNumber(this.totalPaymentAmount)} ${this.browserCurrency}`);
            }
          }
        }
      },

      refreshWalletBalance: function () {
        this.loadWalletBalance();
        if (this.$toast) {
          this.$toast.info('Refreshing wallet balance...');
        }
      },

      loadWalletBalance: function () {
        var self = this;
        var userPublicId = localStorage.getItem('user_public_id');
        if (userPublicId && userPublicId !== 'null' && userPublicId !== 'undefined') {
          this.walletLoading = true;
          this.$store.dispatch('get_wallet_balance', userPublicId).then(function (res) {
            if (res && res.status === 'success') {
              self.walletBalanceUsd = parseFloat(res.data.wallet_balance_usd) || 0;
              self.walletBalanceKes = parseFloat(res.data.wallet_balance_kes) || 0;
            }
            self.walletLoading = false;
            self.$forceUpdate();
          }).catch(function (err) {
            self.walletLoading = false;
          });
        }
      },

      removeDonation: function (index) {
        this.loadedDonationsList.splice(index, 1);
        localStorage.setItem('donation_data', JSON.stringify(this.loadedDonationsList));
        this.$forceUpdate();
        if (this.$toast) {
          this.$toast.info('Donation removed');
        }
      },

      processWalletPayment: function () {
        var self = this;

        var totalAmount = 0;
        var currencyToUse = '';

        if (this.bookingCurrency === 'USD') {
          totalAmount = this.totalPaymentAmountUSD;
          currencyToUse = 'USD';
        } else {
          totalAmount = this.totalPaymentAmount;
          currencyToUse = 'KES';
        }

        var hasBalance = false;
        if (currencyToUse === 'USD') {
          hasBalance = this.walletBalanceUsd >= totalAmount;
        } else {
          hasBalance = this.walletBalanceKes >= totalAmount;
        }

        if (!hasBalance) {
          if (this.$toast) {
            this.$toast.error(`Insufficient ${currencyToUse} balance. Need ${totalAmount.toFixed(2)} ${currencyToUse}`);
          }
          return;
        }

        if (totalAmount <= 0) {
          if (this.$toast) this.$toast.error('Invalid payment amount');
          return;
        }

        this.processingWalletPayment = true;
        var userPublicId = localStorage.getItem('user_public_id');
        var bookingId = this.singleBooking ? this.singleBooking.booking_public_id : this.$route.params.booking_id;

        if (!userPublicId) {
          if (this.$toast) this.$toast.error('Please log in to use wallet payment');
          this.processingWalletPayment = false;
          return;
        }

        var bookingRefCode = null;
        if (this.singleBooking) {
          bookingRefCode = this.singleBooking.booking_reference ||
            this.singleBooking.booking_ref_code ||
            this.singleBooking.reference ||
            this.singleBooking.booking_code ||
            this.singleBooking.code;
        }

        if (!bookingRefCode) {
          bookingRefCode = 'BK-' + bookingId.substring(0, 8);
        }

        var paymentData = {
          user_public_id: userPublicId,
          amount: totalAmount,
          currency: currencyToUse,
          booking_id: bookingId,
          booking_ref_code: bookingRefCode,
          reference: 'WALLET-PAY-' + Date.now() + '-' + Math.random().toString(36).substr(2, 8),
          description: 'Payment for booking ' + bookingId,
          payment_method: '8ty34gh',
          session_id: localStorage.getItem("lkjhgfdsa") || 'guest',
        };

        this.$store.dispatch('pay_from_wallet', paymentData)
          .then(function (response) {
            if (response && response.status === 'success') {
              if (self.loadedDonationsList.length > 0) {
                var donation_infor = [];

                if (self.usdDonationsTotal > 0) {
                  donation_infor.push({
                    new_donation_currency_id: '6bf74e49-7ff6-4555-a080-15541d62204a',
                    new_donation_currency_name: 'USD',
                    new_donation_amount: self.usdDonationsTotal,
                  });
                }

                if (self.kesDonationsTotal > 0) {
                  donation_infor.push({
                    new_donation_currency_id: '162fface-f5f1-41de-913b-d2bb784dda3a',
                    new_donation_currency_name: 'KES',
                    new_donation_amount: self.kesDonationsTotal,
                  });
                }

                var payment_donations = {
                  public_donations: donation_infor,
                  booking_id: bookingId,
                  booking_ref_code: bookingRefCode,
                  payment_method: '8ty34gh',
                  payment_status: 1,
                  payment_reference: response.payment_reference || paymentData.reference,
                  session_id: localStorage.getItem("lkjhgfdsa") || 'guest',
                  user_public_id: userPublicId,
                };

                self.$store.dispatch("post_iveri_donation", payment_donations);
              }

              if (self.$toast) self.$toast.success('Payment successful! Redirecting...');
              localStorage.removeItem('donation_data');

              setTimeout(function () {
                window.location.href = '/guest-success/' + bookingId;
              }, 2000);
            } else {
              var errorMsg = (response && response.message) || 'Payment failed';
              if (self.$toast) self.$toast.error(errorMsg);
              self.processingWalletPayment = false;
            }
          })
          .catch(function (error) {
            var errorMsg = 'Payment processing failed';
            if (error.response && error.response.data && error.response.data.message) {
              errorMsg = error.response.data.message;
            } else if (error.message) {
              errorMsg = error.message;
            }
            if (self.$toast) self.$toast.error(errorMsg);
            self.processingWalletPayment = false;
          });
      },

      loadDonationsFromStorage: function () {
        var savedDonations = localStorage.getItem('donation_data');
        if (savedDonations && savedDonations !== 'null' && savedDonations !== '[]') {
          try {
            this.loadedDonationsList = JSON.parse(savedDonations);
            if (this.$store.commit && this.loadedDonationsList.length > 0) {
              this.$store.commit('PUBLIC_DONATION', this.loadedDonationsList);
            }
          } catch (e) {
          }
        } else {
          var donationStore = this.$store.state.DonationStore;
          var storeDonations = donationStore && donationStore.public_donation ? donationStore.public_donation : [];
          if (storeDonations.length > 0) {
            this.loadedDonationsList = storeDonations;
          }
        }
      },

      checkPayment: function () {
        this.$store.dispatch("loading_true");
        this.$store.dispatch("get_booking", this.$route.params.booking_id);
        this.payment_mode = "Mpesa";
      },

      requestPayment: function () {
        if (!this.mpesa.phone_number || this.mpesa.phone_number.length < 10) {
          if (this.$toast) {
            this.$toast.error('Please enter a valid phone number (e.g., 254712345678)');
          }
          return;
        }

        this.payment_requested = true;
        var donation_infor = [];

        if (this.loadedDonationsList.length > 0) {
          var total_donation = this.loadedDonationsTotal;
          donation_infor = [{
            new_donation_currency_id: this.browserCurrencyID,
            new_donation_currency_name: this.browserCurrency,
            new_donation_amount: total_donation,
            new_donation_currency_buy_rate: 1,
            new_donation_currency_sell_rate: 1,
          }];
        }

        var bookingId = this.$route.params.booking_id;
        var bookingRefCode = this.singleBooking ?
          (this.singleBooking.booking_reference ||
            this.singleBooking.booking_ref_code ||
            this.singleBooking.reference ||
            this.singleBooking.booking_code ||
            this.singleBooking.code ||
            'BK-' + bookingId.substring(0, 8)) :
          'BK-' + bookingId.substring(0, 8);

        var data = {
          phone: this.mpesa.phone_number,
          booking_id: bookingId,
          public_donations: donation_infor,
          booking_ref_code: bookingRefCode,
          payment_method: 'emkrer',
          payment_status: 0,
          session_id: localStorage.getItem("lkjhgfdsa"),
        };

        this.$store.dispatch("booking_mpesa_request", data);
      },

      changeCurrencyDonation: function (currencyObj) {
        if (currencyObj) {
          this.on_payment_donation.donation_currency_obj = currencyObj;
          this.on_payment_donation.donation_currency_id = currencyObj.currency_public_id;
          this.on_payment_donation.donation_currency_name = currencyObj.currency_name;
          var buyingSelling = currencyObj.buying_selling;
          this.on_payment_donation.donation_currency_buy_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].buy_amount : 1;
          this.on_payment_donation.donation_currency_sell_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].sell_amount : 1;
        }
      },

      setDefaultDonationCurrency: function () {
        var bookingCurr = this.bookingCurrency;
        if (bookingCurr && this.allCurrencies.length > 0) {
          var currencyObj = this.allCurrencies.find(function (c) {
            return c.currency_name === bookingCurr;
          });
          if (currencyObj) {
            this.on_payment_donation.donation_currency_obj = currencyObj;
            this.on_payment_donation.donation_currency_id = currencyObj.currency_public_id;
            this.on_payment_donation.donation_currency_name = currencyObj.currency_name;
            var buyingSelling = currencyObj.buying_selling;
            this.on_payment_donation.donation_currency_buy_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].buy_amount : 1;
            this.on_payment_donation.donation_currency_sell_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].sell_amount : 1;
            this.currencySet = true;
            this.$forceUpdate();
            return true;
          }
        }
        return false;
      },

      addOnlinePaymentDonation: function () {
        // Always use the booking currency
        var bookingCurr = this.bookingCurrency;

        // Set the currency to the booking currency
        var currencyObj = this.allCurrencies.find(function (c) {
          return c.currency_name === bookingCurr;
        });

        if (currencyObj) {
          this.on_payment_donation.donation_currency_obj = currencyObj;
          this.on_payment_donation.donation_currency_id = currencyObj.currency_public_id;
          this.on_payment_donation.donation_currency_name = currencyObj.currency_name;
          var buyingSelling = currencyObj.buying_selling;
          this.on_payment_donation.donation_currency_buy_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].buy_amount : 1;
          this.on_payment_donation.donation_currency_sell_rate = (buyingSelling && buyingSelling[0]) ? buyingSelling[0].sell_amount : 1;
          this.currencySet = true;
        } else {
          if (this.$toast) this.$toast.error('Please wait, currency is loading...');
          return;
        }

        if (!this.on_payment_donation.donation_amount || this.on_payment_donation.donation_amount <= 0) {
          if (this.$toast) this.$toast.error('Please enter a valid donation amount');
          return;
        }

        var donationAmount = parseFloat(this.on_payment_donation.donation_amount);
        var donationCurrency = this.on_payment_donation.donation_currency_name;

        var online_donation = {
          new_donation_currency_id: this.on_payment_donation.donation_currency_id,
          new_donation_currency_name: donationCurrency,
          new_donation_amount: donationAmount,
          new_donation_currency_buy_rate: this.on_payment_donation.donation_currency_buy_rate || 1,
          new_donation_currency_sell_rate: this.on_payment_donation.donation_currency_sell_rate || 1,
          session_id: localStorage.getItem("lkjhgfdsa") || 'guest',
          user_public_id: localStorage.getItem('user_public_id') || '',
        };

        this.loadedDonationsList.push({
          amount: donationAmount,
          currency_id: this.on_payment_donation.donation_currency_id,
          currency_name: donationCurrency,
          cause_name: 'Conservation Donation'
        });

        localStorage.setItem('donation_data', JSON.stringify(this.loadedDonationsList));
        this.$store.commit("ADD_ONLINE_DONATION", online_donation);

        if (this.$toast) {
          this.$toast.success(`Donation of ${donationCurrency} ${donationAmount} added successfully!`);
        }

        this.on_payment_donation.donation_amount = "";
        this.$forceUpdate();
      },

      checkForPaymentDonation: function () {
        var donation_infor = [];
        if (this.loadedDonationsList.length > 0) {
          var total_donation = this.loadedDonationsTotal;
          donation_infor = [{
            new_donation_currency_id: this.browserCurrencyID,
            new_donation_currency_name: this.browserCurrency,
            new_donation_amount: total_donation,
          }];
        }

        var bookingId = this.singleBooking ? this.singleBooking.booking_public_id : this.$route.params.booking_id;
        var bookingRefCode = this.singleBooking ?
          (this.singleBooking.booking_reference ||
            this.singleBooking.booking_ref_code ||
            this.singleBooking.reference ||
            this.singleBooking.booking_code ||
            this.singleBooking.code ||
            'BK-' + bookingId.substring(0, 8)) :
          'BK-' + bookingId.substring(0, 8);

        var cardRef = 'CARD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 8);

        var payment_donations = {
          public_donations: donation_infor,
          booking_id: bookingId,
          booking_ref_code: bookingRefCode,
          payment_method: 'enjkr4k4',
          payment_status: 0,
          payment_reference: cardRef,
          session_id: localStorage.getItem("lkjhgfdsa") || 'guest',
          user_public_id: localStorage.getItem('user_public_id') || '',
          card_tx_code: cardRef
        };
        this.$store.dispatch("post_iveri_donation", payment_donations);

        this.cardPaymentReference = cardRef;
      },

      loadUserData: function () {
        this.paymentEmail = localStorage.getItem('user_email') || localStorage.getItem('email') || '';
        var phone = localStorage.getItem('user_phone') || localStorage.getItem('user_phone_number') || localStorage.getItem('phone') || '';
        if (phone && !this.mpesa.phone_number) {
          this.mpesa.phone_number = phone;
        }
      },

      currencyChange: function (currency) {
        if (this.$store.commit) {
          this.$store.commit('SET_BROWSER_CURRENCY', currency);
        }
      },
    },

    beforeCreate: function () {
      if (this.$store.dispatch) {
        this.$store.dispatch("get_payment_platforms");
        this.$store.dispatch("get_jenga_auth");
        this.$store.dispatch("get_all_currencies");
        this.$store.dispatch("get_currency_exchange_rates");
      }
    },

    created: function () {
      var self = this;
      this.loadUserData();
      this.loadDonationsFromStorage();
      this.loadWalletBalance();

      if (this.$store.dispatch) {
        this.$store.dispatch("get_booking", this.$route.params.booking_id);
        this.$store.dispatch("get_all_currencies");
        this.$store.dispatch("get_currency_exchange_rates");
      }

      // Set currency immediately
      this.setDefaultDonationCurrency();

      // Also set after delays to ensure data is loaded
      setTimeout(function () {
        if (!self.currencySet) {
          self.setDefaultDonationCurrency();
        }
        self.$forceUpdate();
      }, 500);

      setTimeout(function () {
        if (!self.currencySet) {
          self.setDefaultDonationCurrency();
          self.$forceUpdate();
        }
      }, 1500);
    },
  };
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('../PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  .checkout-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
  }

  .payment-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px 0;
    position: relative;
  }

  .earth-color {
    color: #74a533;
  }

  .sidebar-col {
    padding-right: 20px;
  }

  .main-content {
    background-color: white;
    border-radius: 10px;
    padding: 25px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    margin-left: 20px;
  }

  .donation-card, .wallet-info-card, .booking-summary-card {
    background-color: #f5f9f0;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 25px;
    border: 1px solid #e0e8d0;
  }

  .donation-title, .wallet-title, .summary-title, .methods-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.3rem;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    color: #4a6b22;
  }

  .wallet-balances {
    display: flex;
    gap: 30px;
    flex-wrap: wrap;
  }

  .wallet-balance-item {
    padding: 10px 15px;
    background: white;
    border-radius: 8px;
    flex: 1;
    min-width: 150px;
  }

  .balance-label {
    font-weight: 500;
    color: #666;
    display: block;
    font-size: 0.85rem;
  }

  .balance-amount {
    font-size: 1.3rem;
    font-weight: bold;
    display: block;
  }

  .text-success {
    color: #28a745;
  }

  .text-danger {
    color: #dc3545;
  }

  .summary-details {
    padding: 10px;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #e0e0e0;
  }

    .summary-row.total-row, .summary-row.grand-total-row {
      border-bottom: none;
      font-size: 1.1rem;
    }

  .grand-total-row {
    background-color: #e8f5e9;
    padding: 10px;
    border-radius: 5px;
    margin-top: 5px;
  }

  .method-cards {
    margin: 0 -10px;
  }

  .method-card {
    border-radius: 10px;
    margin: 10px;
    transition: all 0.3s ease;
    cursor: pointer;
    border: 2px solid transparent;
    height: 100%;
  }

    .method-card:hover:not(.disabled-method) {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    }

  .active-method {
    border-color: #74a533;
    background-color: #f5f9f0;
  }

  .selected-method {
    border: 2px solid #22d219ff !important;
  }

  .disabled-method {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .method-icon {
    padding: 20px;
    text-align: center;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

    .method-icon img {
      max-width: 80%;
      max-height: 80px;
      object-fit: contain;
    }

  .method-description {
    color: #666;
    font-size: 0.85rem;
  }

  .method-limit {
    color: #ff9800;
    font-size: 0.75rem;
    margin-top: 5px;
  }

  .wallet-balance-small {
    font-size: 0.8rem;
    color: #4a6b22;
    margin-top: 5px;
  }

  .sufficient-text {
    color: #155724;
    background-color: #d4edda;
    font-size: 0.75rem;
    margin-top: 5px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 500;
  }

  .insufficient-text {
    color: #721c24;
    background-color: #f8d7da;
    font-size: 0.75rem;
    margin-top: 5px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: 500;
  }

  .method-selection {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px 16px;
  }

  .selected-indicator {
    display: flex;
    align-items: center;
    color: #1976D2;
    font-weight: 500;
  }

  .payment-form {
    margin-top: 25px;
  }

  .form-card {
    border-radius: 10px;
    overflow: hidden;
  }

  .form-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.2rem;
    padding: 15px 20px;
    background-color: #f5f9f0;
  }

  .payment-actions {
    margin: 20px 0;
  }

  .payment-security {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #666;
    font-size: 0.85rem;
  }

  .breakdown-item {
    display: flex;
    justify-content: space-between;
    padding: 5px 0;
  }

    .breakdown-item.total {
      border-top: 1px solid #ddd;
      margin-top: 5px;
      padding-top: 10px;
      font-size: 1.1rem;
    }

  .mpesa-notice {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    background-color: #fff8e1;
    border-radius: 5px;
    margin-bottom: 20px;
  }

  .confirm-payment {
    margin-top: 20px;
  }

  .payment-instructions {
    margin-top: 10px;
    text-align: center;
    font-size: 0.85rem;
    color: #666;
  }

  .email-confirmation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px;
    background-color: #fff8e1;
    border-radius: 5px;
    margin-top: 15px;
    font-size: 0.85rem;
  }

  .donations-total {
    text-align: right;
    padding-top: 10px;
    margin-top: 10px;
    border-top: 1px solid #e0e0e0;
  }

  .donation-button-container {
    padding-top: 0 !important;
  }

  .full-width-btn {
    width: 100%;
    margin-top: 0;
  }

  .donations-list {
    background-color: transparent !important;
  }

  .donation-list-item {
    border-bottom: 1px solid #e0e8d0;
    margin-bottom: 8px;
    padding: 8px 0;
  }

    .donation-list-item:last-child {
      border-bottom: none;
    }

  .donation-item-title {
    font-size: 1rem;
  }

  .slide-fade-enter-active, .slide-fade-leave-active {
    transition: all 0.3s ease;
  }

  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }

  @media (max-width: 960px) {
    .sidebar-col {
      display: none;
    }

    .main-content {
      margin-left: 0 !important;
      width: 100%;
    }

    .checkout-container {
      padding: 15px;
    }

    .payment-wrapper {
      padding: 15px 0;
    }
  }

  @media (max-width: 600px) {
    .checkout-container {
      padding: 10px;
    }

    .payment-wrapper {
      padding: 10px 0;
    }

    .main-content {
      padding: 15px;
    }

    .wallet-balances {
      flex-direction: column;
      gap: 10px;
    }

    .donation-title, .wallet-title, .summary-title, .methods-title {
      font-size: 1.1rem;
    }

    .method-card {
      margin: 5px;
    }
  }
</style>
